import { useState, useEffect, useRef } from 'react';
import { X, CheckCircle2, XCircle, Loader2, FileText } from 'lucide-react';
import { fetchWritingPrompt, submitWritingSample } from '../lib/api';
import { TRUST_VERIFICATION_DELTAS } from '@swipeopinion/shared';

const MIN_CHARS = 100;
const MAX_CHARS = 500;

interface Props {
  onClose: () => void;
}

type Step = 'loading' | 'writing' | 'analyzing' | 'result';

export default function WritingSampleModal({ onClose }: Props) {
  const [step, setStep] = useState<Step>('loading');
  const [prompt, setPrompt] = useState('');
  const [text, setText] = useState('');
  const [result, setResult] = useState<{
    passed: boolean;
    score: number;
    feedback: string;
    attemptsRemaining: number;
    pointsAwarded?: boolean;
    trustAwarded?: boolean;
  } | null>(null);

  const charCount = text.trim().length;
  const canSubmit = charCount >= MIN_CHARS && charCount <= MAX_CHARS;

  // Silent behavioral capture (refs → no re-render, no visible UI). A genuine
  // in-box typist grows the field by keystroke; a paste/AI dump appears wholesale.
  // These are a soft signal; the server also uses Claude's isCopyPaste/isLikelyAI
  // and the cross-account hash. Spoofable, so treated as a hint, not a hard gate.
  const wasPastedRef = useRef(false);
  const pastedCharsRef = useRef(0);
  const keystrokeCountRef = useRef(0);
  const firstInputAtRef = useRef<number | null>(null);

  function markFirstInput() {
    if (firstInputAtRef.current === null) firstInputAtRef.current = Date.now();
  }
  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    markFirstInput();
    if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Enter') keystrokeCountRef.current += 1;
  }
  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    markFirstInput();
    wasPastedRef.current = true;
    pastedCharsRef.current += e.clipboardData.getData('text').length;
  }

  // Fetch prompt on mount
  useEffect(() => {
    fetchWritingPrompt()
      .then((data) => {
        setPrompt(data.prompt);
        setStep('writing');
      })
      .catch(() => {
        setPrompt('Tell us about a product or service you use regularly. What keeps you coming back to it?');
        setStep('writing');
      });
  }, []);

  function handleChange(value: string) {
    markFirstInput();
    if (value.length <= MAX_CHARS) setText(value);
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    setStep('analyzing');

    const compositionMs = firstInputAtRef.current ? Date.now() - firstInputAtRef.current : 0;
    const res = await submitWritingSample(text.trim(), prompt, {
      wasPasted: wasPastedRef.current,
      pastedChars: pastedCharsRef.current,
      keystrokeCount: keystrokeCountRef.current,
      compositionMs,
    });
    setResult(res);
    setStep('result');

    if (res.passed) {
      setTimeout(() => onClose(), 3000);
    }
  }

  if (step === 'loading') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-8 text-center">
          <Loader2 className="w-8 h-8 text-[#5B4FE8] mx-auto animate-spin" />
          <p className="text-sm text-[#6B7280] mt-3">Loading your prompt...</p>
        </div>
      </div>
    );
  }

  if (step === 'analyzing') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-8 text-center space-y-3">
          <Loader2 className="w-10 h-10 text-[#5B4FE8] mx-auto animate-spin" />
          <h3 className="text-lg font-bold text-[#111827]">Analyzing...</h3>
          <p className="text-sm text-[#6B7280]">Evaluating your response for authenticity</p>
        </div>
      </div>
    );
  }

  if (step === 'result' && result) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-8 text-center space-y-4">
          {result.passed ? (
            <>
              <CheckCircle2 className="w-14 h-14 text-[#10B981] mx-auto" />
              <h3 className="text-lg font-bold text-[#111827]">Writing sample verified!</h3>
              {(() => {
                // Honest: list ONLY what actually landed. If the backend didn't
                // report (legacy), fall back to the full award line.
                const reported = result.pointsAwarded !== undefined || result.trustAwarded !== undefined;
                if (!reported) {
                  return <p className="text-sm text-[#10B981] font-medium">+{TRUST_VERIFICATION_DELTAS.WRITING_SAMPLE} trust score and 20 reward points added</p>;
                }
                const parts: string[] = [];
                if (result.trustAwarded) parts.push(`+${TRUST_VERIFICATION_DELTAS.WRITING_SAMPLE} trust score`);
                if (result.pointsAwarded) parts.push('20 reward points');
                return parts.length > 0
                  ? <p className="text-sm text-[#10B981] font-medium">{parts.join(' and ')} added</p>
                  : <p className="text-sm text-[#6B7280]">Your submission was received.</p>;
              })()}
            </>
          ) : (
            <>
              <XCircle className="w-14 h-14 text-[#EF4444] mx-auto" />
              <h3 className="text-lg font-bold text-[#111827]">Verification failed</h3>
              <p className="text-sm text-[#6B7280]">{result.feedback}</p>
              {result.attemptsRemaining > 0 ? (
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => { setText(''); setResult(null); setStep('writing'); }}
                    className="flex-1 py-3 rounded-full bg-[#5B4FE8] text-white text-sm font-medium hover:bg-[#4338CA] transition-colors"
                  >
                    Try Again ({result.attemptsRemaining} left)
                  </button>
                  <a
                    href="mailto:support@swipeopinion.com?subject=Writing%20Sample%20Review%20Request"
                    className="flex-1 py-3 rounded-full border-2 border-[#E5E7EB] text-sm font-medium text-[#6B7280] hover:bg-gray-50 flex items-center justify-center"
                  >
                    Contact Support
                  </a>
                </div>
              ) : (
                <div className="space-y-3 pt-2">
                  <p className="text-xs text-[#6B7280]">If you believe this is an error, please contact customer support to have your sample reviewed manually.</p>
                  <a
                    href="mailto:support@swipeopinion.com?subject=Writing%20Sample%20Review%20Request"
                    className="w-full inline-block py-3 rounded-full border-2 border-[#E5E7EB] text-sm font-medium text-[#6B7280] hover:bg-gray-50 text-center"
                  >
                    Contact Support
                  </a>
                </div>
              )}
            </>
          )}
          {result.passed && (
            <button onClick={onClose} className="w-full py-3 rounded-full bg-[#5B4FE8] text-white text-sm font-medium hover:bg-[#4338CA] transition-colors">
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  // Writing step
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="bg-[#5B4FE8] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-200" />
            <h2 className="text-white font-bold text-sm">Writing Sample</h2>
          </div>
          <button onClick={onClose} className="text-purple-200 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 py-6 space-y-5">
          <div className="bg-[#EEF2FF] rounded-xl p-4 border border-[#5B4FE8]/10">
            <p className="text-xs text-[#5B4FE8] font-bold uppercase tracking-wide mb-1">Your prompt</p>
            <p className="text-sm text-[#111827] leading-relaxed">{prompt}</p>
          </div>

          <textarea
            value={text}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            rows={6}
            maxLength={MAX_CHARS}
            className="w-full border border-[#E5E7EB] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#5B4FE8] focus:ring-2 focus:ring-purple-100 resize-none"
            placeholder="Write your response in your own words..."
          />

          <div className="flex items-center justify-between">
            <p className={`text-sm font-medium tabular-nums ${charCount >= MIN_CHARS ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
              {charCount} / {MIN_CHARS} characters minimum
            </p>
            <p className="text-xs text-[#6B7280] tabular-nums">{MAX_CHARS - text.length} remaining</p>
          </div>

          <p className="text-[10px] text-[#6B7280]">Write in your own words. Responses are analyzed for authenticity.</p>

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-3 rounded-full border-2 border-[#E5E7EB] text-sm font-medium text-[#111827] hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={() => void handleSubmit()}
              disabled={!canSubmit}
              className="flex-1 py-3 rounded-full bg-[#5B4FE8] text-white text-sm font-medium disabled:opacity-40 hover:bg-[#4338CA] transition-colors"
            >
              Submit for Review
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
